import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import {
  Avatar, AvatarGroup, Box, Button, Collapse, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay,
  Flex, HStack, Icon, Input, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal,
  SimpleGrid, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Spinner, Switch, Text, Textarea, useDisclosure, VStack, Wrap, WrapItem
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { motion, AnimatePresence } from "framer-motion";
import {
  PiCalendarBlankDuotone, PiHourglassMediumDuotone, PiCaretDownBold,
  PiPlayCircleDuotone, PiTimerDuotone, PiEyeDuotone, PiGlobeDuotone, PiEyeSlashDuotone, PiLockDuotone, PiUsersThreeDuotone,
  PiCheckBold, PiPencilSimpleDuotone, PiSealCheckDuotone, PiCheckCircleDuotone, PiEnvelopeSimpleDuotone, PiDeviceMobileDuotone,
  PiFingerprintDuotone, PiLockOpenDuotone, PiKeyDuotone, PiShieldCheckDuotone,
  PiIdentificationCardDuotone, PiTicketDuotone, PiSignInDuotone, PiSlidersHorizontalDuotone,
  PiUploadSimpleDuotone, PiUserPlusDuotone, PiUsersFourDuotone, PiDownloadSimpleDuotone, PiMagnifyingGlassDuotone,
  PiListChecksDuotone,
  PiPercentDuotone, PiFileTextDuotone, PiTrashDuotone, PiPuzzlePieceDuotone
} from "react-icons/pi";
import { DATE_KEYS, VOTING_DATA_ERR_KEYS, getDateObject } from "@lubbezposrednio-frontend/core";
import { CreateVotingViewModel } from "../CreateVotingView/CreateVotingViewModel";
import { FieldError } from "../CreateVotingView/components/stepFields";
import { AddVotersModal } from "../CreateVotingView/components/EligibleVoters/components/AddVotersModal";
import { StepQuestions } from "./StepQuestions";
import { GLASS, drgba, EASE } from "../../components/glassUi";

const START = "#10b981";
const DUR = "#7b2ff7";
const END = "#e51f44";
const NAME = "#2563eb";
const EASE_ARR = [0.22, 1, 0.36, 1] as const;

const MIN = 60 * 1000, HOUR = 60 * MIN, DAY = 24 * HOUR;
const pad = (n: number) => String(n).padStart(2, "0");
type OnChange = (key: DATE_KEYS, value: string) => void;

const setDate = (date: Date, onChange: OnChange) => {
  onChange(DATE_KEYS.YEAR, String(date.getFullYear()));
  onChange(DATE_KEYS.MONTH, String(date.getMonth() + 1));
  onChange(DATE_KEYS.DAY, String(date.getDate()));
  onChange(DATE_KEYS.HOUR, String(date.getHours()));
  onChange(DATE_KEYS.MINUTES, String(date.getMinutes()));
};
const isComplete = (d: Record<DATE_KEYS, string>) => Object.values(d).every(v => v !== "" && v != null);
const toInputValue = (d: Record<DATE_KEYS, string>): string => {
  const y = d[DATE_KEYS.YEAR], mo = d[DATE_KEYS.MONTH], da = d[DATE_KEYS.DAY], h = d[DATE_KEYS.HOUR], mi = d[DATE_KEYS.MINUTES];
  if ([y, mo, da, h, mi].some(v => v === "" || v == null)) return "";
  return `${String(+y).padStart(4, "0")}-${pad(+mo)}-${pad(+da)}T${pad(+h)}:${pad(+mi)}`;
};
const fromInput = (val: string, onChange: OnChange) => {
  if (!val) { Object.values(DATE_KEYS).forEach(k => onChange(k, "")); return; }
  const [dp, tp] = val.split("T");
  const [y, mo, da] = dp.split("-");
  const [h, mi] = (tp || "00:00").split(":");
  onChange(DATE_KEYS.YEAR, String(+y)); onChange(DATE_KEYS.MONTH, String(+mo)); onChange(DATE_KEYS.DAY, String(+da));
  onChange(DATE_KEYS.HOUR, String(+h)); onChange(DATE_KEYS.MINUTES, String(+mi));
};
const nextWeekday = (weekday: number, hour: number) => {
  const d = new Date(); d.setHours(hour, 0, 0, 0);
  const cur = d.getDay() === 0 ? 7 : d.getDay();
  let add = weekday - cur; if (add <= 0) add += 7;
  d.setDate(d.getDate() + add); return d;
};
const defaultStart = () => { const d = new Date(); d.setSeconds(0, 0); d.setMinutes(d.getMinutes() + 15); d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5); return d; };
const nowInputMin = () => { const n = new Date(); return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}T${pad(n.getHours())}:${pad(n.getMinutes())}`; };
const dateToLocal = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const fmtShort = (d: Date) => {
  try { return new Intl.DateTimeFormat("pl-PL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(d).replace(/\./g, ""); }
  catch { return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; }
};
const fmtDur = (ms: number) => {
  const days = Math.floor(ms / DAY), hours = Math.floor((ms % DAY) / HOUR), mins = Math.floor((ms % HOUR) / MIN);
  const parts: string[] = [];
  if (days) parts.push(days === 1 ? "1 dzień" : `${days} dni`);
  if (hours) parts.push(hours === 1 ? "1 godz." : `${hours} godz.`);
  if (mins) parts.push(`${mins} min`);
  return parts.join(" ") || "0 min";
};

// ── Klikalny token w zdaniu (pigułka otwierająca popover) ──
const Token: React.FC<{
  color: string; icon: React.ElementType; value?: string; placeholder: string; title: string; titleIcon: React.ElementType;
  invalid?: boolean; children: (close: () => void) => React.ReactNode;
}> = ({ color, icon, value, placeholder, title, titleIcon, invalid, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const filled = !!value;
  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" isLazy gutter={8}>
      <PopoverTrigger>
        <Box
          as="button" type="button" display="inline-flex" alignItems="center"
          gap="7px" px="11px" h="34px" borderRadius="10px"
          bg={filled ? drgba(color, 0.1) : "rgba(24,12,48,0.035)"}
          border="1.5px solid" borderColor={invalid ? END : filled ? drgba(color, 0.3) : "rgba(24,12,48,0.1)"}
          transition={`all ${EASE}`}
          _hover={{ bg: drgba(color, 0.14), borderColor: color, transform: "translateY(-1px)", boxShadow: `0 8px 20px -12px ${drgba(color, 0.6)}` }}
          _active={{ transform: "translateY(0)" }}
          boxShadow={isOpen ? `0 0 0 3px ${drgba(color, 0.2)}` : undefined}
        >
          <Icon as={icon} boxSize="16px" color={filled ? color : "#a29bb2"} />
          <Text fontSize="14px" fontWeight="800" color={filled ? "#1b1030" : "#a29bb2"} whiteSpace="nowrap" letterSpacing="-0.01em">
            {value || placeholder}
          </Text>
          <Icon as={PiCaretDownBold} boxSize="9px" color={filled ? drgba(color, 0.65) : "#c3bdd0"} transform={isOpen ? "rotate(180deg)" : undefined} transition={`transform ${EASE}`} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto" minW="308px" zIndex={1600} borderRadius="20px" border="1px solid rgba(24,12,48,0.08)" overflow="hidden" sx={{ ...GLASS, background: "rgba(255,255,255,0.97)" }} boxShadow="0 24px 60px -24px rgba(43,0,90,0.5)" _focusVisible={{ outline: "none" }}>
          <PopoverArrow bg="white" />
          <PopoverBody p="16px">
            <HStack spacing="8px" mb="14px">
              <Flex w="28px" h="28px" borderRadius="9px" bg={drgba(color, 0.12)} align="center" justify="center">
                <Icon as={titleIcon} boxSize="16px" color={color} />
              </Flex>
              <Text fontSize="14px" fontWeight="800" color="#241536">{title}</Text>
            </HStack>
            {children(onClose)}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const QuickPill: React.FC<{ label: string; onClick: () => void; color: string; active?: boolean }> = ({ label, onClick, color, active }) => (
  <Box
    as="button" type="button" onClick={onClick} px="12px" h="38px" borderRadius="11px" fontWeight="700" fontSize="13px"
    bg={active ? color : drgba(color, 0.09)} color={active ? "white" : "#4a4458"} transition={`all ${EASE}`}
    _hover={{ bg: active ? color : drgba(color, 0.18), transform: "translateY(-1px)" }} _active={{ transform: "translateY(0)" }}
  >
    {label}
  </Box>
);

const PopLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="11px" fontWeight="900" letterSpacing="0.06em" textTransform="uppercase" color="#a29bb2" mb="8px">{children}</Text>
);

// ── Mini-nagłówek sekcji na płótnie kreatora (opcjonalny prawy slot) ──
const SectionHead: React.FC<{ icon: React.ElementType; color: string; children: React.ReactNode; right?: React.ReactNode }> = ({ icon, color, children, right }) => (
  <Flex align="center" justify="space-between" gap="12px" mb="18px">
    <HStack spacing="9px" minW={0}>
      <Icon as={icon} boxSize="18px" color={color} />
      <Text fontSize="14px" fontWeight="800" color="#241536" letterSpacing="-0.01em">{children}</Text>
    </HStack>
    {right}
  </Flex>
);

// ── Klikalny, animowany przełącznik stanu: klik = następna opcja (loop), zamiana ikony+podpisu z animacją ──
type StateOpt = { value: string; icon: React.ElementType; color: string; label: string };
const StateToggle: React.FC<{ options: StateOpt[]; value: string; onChange: (v: string) => void }> = ({ options, value, onChange }) => {
  const idx = Math.max(0, options.findIndex(o => o.value === value));
  const cur = options[idx];
  const next = () => onChange(options[(idx + 1) % options.length].value);
  return (
    <VStack spacing="7px">
      <motion.button
        type="button"
        onClick={next}
        whileTap={{ scale: 0.85 }}
        title={`Kliknij, by zmienić (${options.length} opcje)`}
        style={{ position: "relative", width: 38, height: 38, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", background: drgba(cur.color, 0.13), cursor: "pointer", overflow: "hidden" }}
      >
        <AnimatePresence initial={false}>
          <motion.span
            key={cur.value}
            initial={{ scale: 0, rotate: -110, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            exit={{ scale: 0, rotate: 110, opacity: 0 }}
            transition={{ type: "spring", stiffness: 520, damping: 24 }}
            style={{ position: "absolute", display: "flex" }}
          >
            <Icon as={cur.icon} boxSize="19px" color={cur.color} />
          </motion.span>
        </AnimatePresence>
      </motion.button>
      <Box position="relative" h="15px" minW="70px">
        <AnimatePresence initial={false}>
          <motion.div
            key={cur.value}
            initial={{ y: 9, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -9, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: "absolute", left: 0, right: 0, textAlign: "center" }}
          >
            <Text fontSize="11px" fontWeight="800" color={cur.color} whiteSpace="nowrap">{cur.label}</Text>
          </motion.div>
        </AnimatePresence>
      </Box>
    </VStack>
  );
};

const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="11.5px" fontWeight="800" letterSpacing="0.04em" textTransform="uppercase" color="#a29bb2" mb="10px">{children}</Text>
);

type Opt = { label: string; desc: string; icon: React.ElementType; color: string };
const OptionCard: React.FC<{ opt: Opt; active: boolean; onClick: () => void }> = ({ opt, active, onClick }) => (
  <Box
    as="button" type="button" onClick={onClick} textAlign="left" position="relative" p="14px 15px" borderRadius="15px"
    border="1.5px solid" borderColor={active ? opt.color : "rgba(24,12,48,0.1)"} bg={active ? drgba(opt.color, 0.08) : "rgba(255,255,255,0.5)"}
    transition={`all ${EASE}`} _hover={{ borderColor: opt.color, bg: drgba(opt.color, active ? 0.1 : 0.05), transform: "translateY(-1px)" }} _active={{ transform: "translateY(0)" }}
  >
    <HStack spacing="11px" align="flex-start">
      <Flex w="38px" h="38px" borderRadius="11px" align="center" justify="center" bg={active ? opt.color : drgba(opt.color, 0.12)} flexShrink={0} transition={`all ${EASE}`}>
        <Icon as={opt.icon} boxSize="20px" color={active ? "white" : opt.color} />
      </Flex>
      <Box pr="16px">
        <Text fontSize="14px" fontWeight="800" color="#241536" lineHeight="1.2">{opt.label}</Text>
        <Text fontSize="12px" color="#8a8397" mt="3px" lineHeight="1.4">{opt.desc}</Text>
      </Box>
    </HStack>
    {active && (
      <Flex position="absolute" top="10px" right="10px" w="18px" h="18px" borderRadius="full" bg={opt.color} align="center" justify="center">
        <Icon as={PiCheckBold} boxSize="10px" color="white" />
      </Flex>
    )}
  </Box>
);


// Kanały potwierdzania głosu (3 opcje → kółko robi loop). Współzależne z portfelem i uprawnionymi.
const CONFIRM_OPTS = [
  { value: "in_session", icon: PiCheckCircleDuotone, color: "#0d9488", label: "W sesji", desc: "Zalogowany potwierdza od razu w panelu; osoby bez konta — mailem." },
  { value: "email", icon: PiEnvelopeSimpleDuotone, color: "#2563eb", label: "Mail / apka", desc: "Push w aplikacji, a gdy nie ma sesji — kaskada na e-mail." },
  { value: "app", icon: PiDeviceMobileDuotone, color: "#7b2ff7", label: "Tylko apka", desc: "Wyłącznie push w aplikacji, bez kaskady — najwyższy poziom." }
];

// Wymagana tożsamość wyborcy (2. sekcja) — 3 opcje → loop. „Bez konta" chowa Potwierdzanie i Podpis.
const IDENTITY_OPTS = [
  { value: "none", icon: PiTicketDuotone, color: "#6b7280", label: "Bez konta", desc: "Wystarczy zaproszenie (token/e-mail) — najniższy próg." },
  { value: "account", icon: PiSignInDuotone, color: "#2563eb", label: "Konto", desc: "Głosować mogą tylko zalogowani." },
  { value: "verified", icon: PiSealCheckDuotone, color: "#10b981", label: "Zweryfikowana", desc: "Konto + potwierdzona tożsamość (mObywatel)." },
  { value: "cyberid", icon: PiFingerprintDuotone, color: "#430098", label: "CyberID", desc: "Zweryfikowana tożsamość przez CyberID (A2/A3) — bez zakładania konta." }
];

const ASSURANCE_OPTS = [
  { value: "2", icon: PiSealCheckDuotone, color: "#10b981", label: "A2", desc: "Dokument / mDowód — standardowy poziom." },
  { value: "3", icon: PiFingerprintDuotone, color: "#430098", label: "A3", desc: "mObywatel — najwyższy poziom zaufania." }
];

// Podpis tożsamością cyfrową (portfel) — dostępny tylko przy kanale „Tylko apka". 3 opcje → loop.
const WALLET_OPTS = [
  { value: "none", icon: PiLockOpenDuotone, color: "#6b7280", label: "Brak", desc: "Potwierdzenie tokenem/loginem wystarczy." },
  { value: "recommended", icon: PiKeyDuotone, color: "#f59e0b", label: "Zalecany", desc: "Apka zachęca do podpisu; bez podpisu też przejdzie." },
  { value: "required", icon: PiShieldCheckDuotone, color: "#e51f44", label: "Wymagany", desc: "Bez podpisu tożsamością nie zatwierdzisz głosu." }
];

// Statyczna okrągła ikona stanu (do zwiniętego rzędu ikon).
const StatePill: React.FC<{ icon: React.ElementType; color: string }> = ({ icon, color }) => (
  <Flex w="34px" h="34px" borderRadius="full" bg={drgba(color, 0.13)} align="center" justify="center" flexShrink={0}>
    <Icon as={icon} boxSize="18px" color={color} />
  </Flex>
);

// Wiersz dokumentu do wgrania (regulamin / polityka) — PDF.
const DocRow: React.FC<{ label: string; color: string; hasFile: boolean; fileName?: string; onPick: (f?: File) => void }> = ({ label, color, hasFile, fileName, onPick }) => {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <Flex align="center" gap="11px" p="11px 13px" borderRadius="13px" bg="rgba(255,255,255,0.6)" border="1.5px solid" borderColor={hasFile ? drgba(color, 0.32) : "rgba(24,12,48,0.08)"} transition={`all ${EASE}`}>
      <input type="file" accept="application/pdf" ref={ref} style={{ display: "none" }} onChange={e => onPick(e.target.files?.[0])} />
      <Flex w="34px" h="34px" borderRadius="10px" bg={drgba(color, 0.12)} align="center" justify="center" flexShrink={0}>
        <Icon as={PiFileTextDuotone} boxSize="17px" color={color} />
      </Flex>
      <Box flex="1" minW={0}>
        <Text fontSize="13.5px" fontWeight="800" color="#241536" noOfLines={1}>{label}</Text>
        <Text fontSize="12px" color={hasFile ? color : "#a29bb2"} noOfLines={1} fontWeight={hasFile ? "700" : "500"}>{hasFile ? fileName : "PDF do akceptacji (opcjonalnie)"}</Text>
      </Box>
      {hasFile ? (
        <HStack spacing="4px" flexShrink={0}>
          <Button size="xs" h="30px" variant="ghost" onClick={() => ref.current?.click()} borderRadius="9px" fontSize="12px" fontWeight="700" color="#5a5468" _hover={{ bg: "rgba(24,12,48,0.05)" }}>Zmień</Button>
          <Button size="xs" h="30px" w="30px" p="0" variant="ghost" onClick={() => onPick(undefined)} borderRadius="9px" color="#c41a3a" _hover={{ bg: drgba("#e51f44", 0.1) }}><Icon as={PiTrashDuotone} boxSize="15px" /></Button>
        </HStack>
      ) : (
        <Button size="xs" h="30px" leftIcon={<Icon as={PiUploadSimpleDuotone} boxSize="14px" />} onClick={() => ref.current?.click()} borderRadius="9px" fontSize="12px" fontWeight="800" bg={drgba(color, 0.1)} color={color} _hover={{ bg: drgba(color, 0.18) }} flexShrink={0}>Wgraj</Button>
      )}
    </Flex>
  );
};

// Szybka akcja w pustym stanie uprawnionych.
const QuickAction: React.FC<{ icon: React.ElementType; label: string; color: string; onClick?: () => void }> = ({ icon, label, color, onClick }) => (
  <Flex as="button" type="button" onClick={onClick} direction="column" align="center" gap="9px" p="18px 12px" borderRadius="14px"
    bg="rgba(255,255,255,0.7)" border="1.5px solid rgba(24,12,48,0.08)" transition={`all ${EASE}`}
    _hover={{ borderColor: color, bg: drgba(color, 0.04), transform: "translateY(-2px)", boxShadow: `0 12px 26px -16px ${drgba(color, 0.55)}` }} _active={{ transform: "translateY(0)" }}>
    <Flex w="42px" h="42px" borderRadius="13px" bg={drgba(color, 0.12)} align="center" justify="center">
      <Icon as={icon} boxSize="22px" color={color} />
    </Flex>
    <Text fontSize="13px" fontWeight="800" color="#241536">{label}</Text>
  </Flex>
);

// Typ elektoratu (kto może głosować) — przełącznik w sekcji Uprawnieni.
const ELECTORATE = [
  { value: "closed", icon: PiUsersThreeDuotone, label: "Lista", color: "#2563eb" },
  { value: "signup", icon: PiUserPlusDuotone, label: "Zapisy", color: "#7b2ff7" },
  { value: "open", icon: PiGlobeDuotone, label: "Otwarte", color: "#10b981" }
];

export const StepData: React.FC<{ model: CreateVotingViewModel; stepper?: React.ReactNode }> = observer(({ model, stepper }) => {
  const d = model.votingDataModel;
  const nameErr = d.errors[VOTING_DATA_ERR_KEYS.NAME];
  const startErr = d.errors[VOTING_DATA_ERR_KEYS.BLUR_START] || d.errors[VOTING_DATA_ERR_KEYS.SUBMIT_START];
  const endErr = d.errors[VOTING_DATA_ERR_KEYS.BLUR_END] || d.errors[VOTING_DATA_ERR_KEYS.SUBMIT_END];

  const startOk = isComplete(d.startVotingDate);
  const endOk = isComplete(d.endVotingDate);
  const startDate = startOk ? getDateObject(d.startVotingDate) : null;
  const endDate = endOk ? getDateObject(d.endVotingDate) : null;
  const durationMs = startDate && endDate ? Math.max(0, endDate.getTime() - startDate.getTime()) : null;

  const [customUnit, setCustomUnit] = useState<"h" | "d">("d");
  const [customVal, setCustomVal] = useState("");
  const visSheet = useDisclosure();
  const identitySheet = useDisclosure();
  const confirmSheet = useDisclosure();
  const walletSheet = useDisclosure();
  const votersModal = useDisclosure();
  const groupsSheet = useDisclosure();
  const csvRef = useRef<HTMLInputElement>(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  useEffect(() => {
    if (groupsSheet.isOpen) { setGroupsLoading(true); Promise.resolve(model.eligibleVotersModel.fetchGroups()).finally(() => setGroupsLoading(false)); }
  }, [groupsSheet.isOpen, model]);
  const onPickCsv = (f?: File) => { if (f) { model.eligibleVotersModel.setVotersCsv(f); model.eligibleVotersModel.validateCsvSize(); } };

  // Cały blok ustawień: zwinięty = jeden rząd ikon; rozwinięty = pełne sekcje. Przejście do tytułu/terminu zwija.
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Pytania (step 3) — licznik wypełnionych (do badge).
  const vq = model.votingQuestionsModel;
  const questionsCount = Array.from(vq.questions.values()).filter(q => q.question.trim()).length;

  // Uprawnieni (step 4) — typ elektoratu + data zamknięcia zapisów.
  const evStartComplete = isComplete(d.startVotingDate);
  const evStartDate = evStartComplete ? getDateObject(d.startVotingDate) : null;
  const evStartLocal = evStartDate ? dateToLocal(evStartDate) : undefined;
  const setSignupBefore = (ms: number) => { if (evStartDate) model.setSignupClosesAt(dateToLocal(new Date(evStartDate.getTime() - ms))); };

  // Tożsamość wyborcy (2. sekcja) — wyliczana z dwóch flag modelu.
  const identityValue = model.verifiedIdentityRequired ? "cyberid" : model.requiresVerification ? "verified" : model.accountRequired ? "account" : "none";
  const curIdentity = IDENTITY_OPTS.find(o => o.value === identityValue) ?? IDENTITY_OPTS[0];
  const hasIdentity = model.accountRequired; // dot. kanału potwierdzania (konto) — CyberID/none potwierdzają mailem
  const applyIdentity = (v: string) => {
    if (v === "cyberid") { model.setVerifiedIdentityRequired(true); model.setAccountRequired(false); model.setRequiresVerification(false); return; }
    model.setVerifiedIdentityRequired(false);
    if (v === "none") { model.setAccountRequired(false); model.setRequiresVerification(false); }
    else if (v === "account") { model.setAccountRequired(true); model.setRequiresVerification(false); }
    else { model.setAccountRequired(true); model.setRequiresVerification(true); }
  };

  const curConfirm = CONFIRM_OPTS.find(o => o.value === model.voteConfirmation) ?? CONFIRM_OPTS[0];
  const isAppOnly = model.voteConfirmation === "app";
  const curWallet = WALLET_OPTS.find(o => o.value === model.walletPolicy) ?? WALLET_OPTS[0];

  // Bi-relacja: brak tożsamości → potwierdzanie mailem (osoby bez konta i tak mailem).
  useEffect(() => {
    if (!hasIdentity && model.voteConfirmation !== "email") model.setVoteConfirmation("email");
  }, [hasIdentity, model]);

  // Współzależność: portfel ma sens tylko przy „Tylko apka" — poza nim wracamy do „Brak".
  useEffect(() => {
    if (!isAppOnly && model.walletPolicy !== "none") model.setWalletPolicy("none");
  }, [isAppOnly, model]);

  // Auto-rosnący tytuł (zawija na wiele linii, rośnie w wysokość).
  const titleRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  }, [d.name]);
  // Auto-rosnący opis.
  const descRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = descRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  }, [d.description]);

  const applyStart = (date: Date) => {
    const keep = durationMs ?? HOUR;
    setDate(date, d.changeStartDate.bind(d));
    setDate(new Date(date.getTime() + keep), d.changeEndDate.bind(d));
  };
  const applyDuration = (ms: number) => {
    let base = startDate;
    if (!base) { base = defaultStart(); setDate(base, d.changeStartDate.bind(d)); }
    setDate(new Date(base.getTime() + ms), d.changeEndDate.bind(d));
  };

  const sentenceFont = { fontSize: "clamp(14px, 1.9vw, 16px)" } as const;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE_ARR }}>
      <Box borderRadius="24px" p={{ base: "26px", md: "48px" }} position="relative" overflow="hidden" sx={GLASS}>
        <Box position="absolute" top="-90px" right="-70px" w="320px" h="320px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba(DUR, 0.09)}, transparent 70%)` }} />

        {/* ── Stepper — pierwszy element w karcie, nad nazwą ── */}
        {stepper && <Box position="relative" mb={{ base: "20px", md: "26px" }}>{stepper}</Box>}

        {/* ── Tytuł osobno — wpisywany inline jak nagłówek, bez chromu inputu ── */}
        <Box position="relative">
          <Textarea
            id="cv-title"
            ref={titleRef}
            value={d.name}
            onChange={e => d.setName(e.target.value)}
            onFocus={() => setSettingsOpen(false)}
            onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
            maxLength={300}
            rows={1}
            placeholder="Nazwa głosowania…"
            variant="unstyled"
            w="100%"
            resize="none"
            overflow="hidden"
            minH="unset"
            fontSize="clamp(27px, 4.4vw, 40px)"
            fontWeight="900"
            color="#0f0820"
            letterSpacing="-0.03em"
            lineHeight="1.14"
            px="8px" py="10px" mx="-8px"
            borderRadius="14px"
            transition={`background ${EASE}`}
            _placeholder={{ color: "#c8c2d6", fontWeight: 900 }}
            _hover={{ bg: "rgba(24,12,48,0.03)" }}
            _focus={{ bg: drgba(NAME, 0.05) }}
            _focusVisible={{ outline: "none", boxShadow: "none" }}
            sx={{ caretColor: NAME }}
          />
          {nameErr && <Box mt="4px"><FieldError>{nameErr}</FieldError></Box>}

          {/* Opis — pod tytułem, seamless, lżejszy */}
          <Textarea
            ref={descRef}
            value={d.description}
            onChange={e => d.setDescription(e.target.value)}
            onFocus={() => setSettingsOpen(false)}
            maxLength={2000}
            rows={1}
            placeholder="Dodaj opis (opcjonalnie) — kontekst, o co chodzi w głosowaniu…"
            variant="unstyled"
            w="100%"
            resize="none"
            overflow="hidden"
            minH="unset"
            mt="6px"
            fontSize="clamp(15px, 1.9vw, 17px)"
            fontWeight="500"
            color="#4a4458"
            lineHeight="1.55"
            px="8px" py="6px" mx="-8px"
            borderRadius="12px"
            transition={`background ${EASE}`}
            _placeholder={{ color: "#b8b2c6", fontWeight: 500 }}
            _hover={{ bg: "rgba(24,12,48,0.025)" }}
            _focus={{ bg: drgba(NAME, 0.04) }}
            _focusVisible={{ outline: "none", boxShadow: "none" }}
            sx={{ caretColor: NAME }}
          />
        </Box>

        {/* ── Ustawienia głosowania: zwinięte = rząd ikon; rozwinięte = pełne sekcje ── */}
        <Box mt={{ base: "26px", md: "34px" }}>
        {!settingsOpen ? (
          <Flex as="button" type="button" onClick={() => setSettingsOpen(true)} w="100%" align="center" justify="space-between" gap="12px"
            bg="rgba(24,12,48,0.028)" borderRadius="18px" px={{ base: "16px", md: "20px" }} py="14px" transition={`background ${EASE}`}
            _hover={{ bg: "rgba(24,12,48,0.05)" }}>
            <HStack spacing="9px" minW={0}>
              <Icon as={PiSlidersHorizontalDuotone} boxSize="18px" color="#7b2ff7" />
              <Text fontSize="14px" fontWeight="800" color="#241536" noOfLines={1}>Ustawienia głosowania</Text>
            </HStack>
            <HStack spacing="8px" flexShrink={0}>
              <StatePill icon={model.isPublic ? PiGlobeDuotone : PiEyeSlashDuotone} color={model.isPublic ? "#2563eb" : "#6b7280"} />
              <StatePill icon={model.isSecret ? PiLockDuotone : PiUsersThreeDuotone} color={model.isSecret ? "#7b2ff7" : "#f59e0b"} />
              <StatePill icon={curIdentity.icon} color={curIdentity.color} />
              {hasIdentity && <StatePill icon={curConfirm.icon} color={curConfirm.color} />}
              {hasIdentity && isAppOnly && <StatePill icon={curWallet.icon} color={curWallet.color} />}
              <Icon as={PiCaretDownBold} boxSize="12px" color="#b8b2c6" ml="2px" />
            </HStack>
          </Flex>
        ) : (
        <VStack align="stretch" spacing="12px">
          {/* 1. Widoczność i tajność */}
          <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
            <SectionHead icon={PiEyeDuotone} color="#2563eb" right={
              <Button size="sm" variant="ghost" leftIcon={<Icon as={PiPencilSimpleDuotone} boxSize="15px" />} onClick={visSheet.onOpen}
                fontSize="12.5px" fontWeight="800" color="#2563eb" bg={drgba("#2563eb", 0.08)} borderRadius="10px" h="34px" px="13px"
                _hover={{ bg: drgba("#2563eb", 0.16) }} _active={{ transform: "scale(0.97)" }}>Zmień</Button>
            }>Widoczność głosowania i tajność głosu</SectionHead>
            <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="18px" direction={{ base: "column", sm: "row" }}>
              <Text fontSize="13.5px" color="#6b6480" lineHeight="1.55" maxW="440px">
                {model.isPublic ? "Publiczne — temat, pytania i wyniki widzi każdy." : "Niepubliczne — temat i wyniki widzą tylko zaproszeni."}{" "}
                {model.isSecret ? "Głos tajny — nie widać, kto jak zagłosował." : "Głos jawny imienny — widać głos każdej osoby."}
              </Text>
              <HStack spacing="14px" flexShrink={0}>
                <StateToggle value={model.isPublic ? "public" : "private"} onChange={v => model.setIsPublic(v === "public")} options={[
                  { value: "private", icon: PiEyeSlashDuotone, color: "#6b7280", label: "Niepubliczne" },
                  { value: "public", icon: PiGlobeDuotone, color: "#2563eb", label: "Publiczne" }
                ]} />
                <StateToggle value={model.isSecret ? "secret" : "open"} onChange={v => model.setIsSecret(v === "secret")} options={[
                  { value: "secret", icon: PiLockDuotone, color: "#7b2ff7", label: "Tajne" },
                  { value: "open", icon: PiUsersThreeDuotone, color: "#f59e0b", label: "Jawne imienne" }
                ]} />
              </HStack>
            </Flex>
          </Box>

          {/* 2. Wymagana tożsamość wyborcy */}
          <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
            <SectionHead icon={PiIdentificationCardDuotone} color="#10b981" right={
              <Button size="sm" variant="ghost" leftIcon={<Icon as={PiPencilSimpleDuotone} boxSize="15px" />} onClick={identitySheet.onOpen}
                fontSize="12.5px" fontWeight="800" color="#10b981" bg={drgba("#10b981", 0.08)} borderRadius="10px" h="34px" px="13px"
                _hover={{ bg: drgba("#10b981", 0.16) }} _active={{ transform: "scale(0.97)" }}>Zmień</Button>
            }>Wymagana tożsamość wyborcy</SectionHead>
            <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="18px" direction={{ base: "column", sm: "row" }}>
              <Text fontSize="13.5px" color="#6b6480" lineHeight="1.55" maxW="440px">
                <Text as="span" fontWeight="800" color="#241536">{curIdentity.label}</Text> — {curIdentity.desc}
              </Text>
              <HStack spacing="14px" flexShrink={0}>
                <StateToggle value={identityValue} onChange={applyIdentity} options={IDENTITY_OPTS} />
              </HStack>
            </Flex>
          </Box>

          {/* 2b. Poziom weryfikacji CyberID — tylko dla tożsamości „CyberID" */}
          <Collapse in={identityValue === "cyberid"} animateOpacity unmountOnExit>
            <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
              <SectionHead icon={PiFingerprintDuotone} color="#430098">Minimalny poziom weryfikacji</SectionHead>
              <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="18px" direction={{ base: "column", sm: "row" }}>
                <Text fontSize="13.5px" color="#6b6480" lineHeight="1.55" maxW="440px">
                  <Text as="span" fontWeight="800" color="#241536">A{model.minAssuranceLevel}</Text> — {model.minAssuranceLevel >= 3 ? "mObywatel (najwyższy poziom zaufania)." : "dokument / mDowód (standard)."}
                </Text>
                <HStack spacing="14px" flexShrink={0}>
                  <StateToggle value={String(model.minAssuranceLevel)} onChange={v => model.setMinAssuranceLevel(Number(v))} options={ASSURANCE_OPTS} />
                </HStack>
              </Flex>
            </Box>
          </Collapse>

          {/* 3. Potwierdzanie głosu — tylko gdy wymagana jakaś tożsamość (bi-relacja) */}
          <Collapse in={hasIdentity} animateOpacity unmountOnExit>
            <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
              <SectionHead icon={PiSealCheckDuotone} color="#0d9488" right={
                <Button size="sm" variant="ghost" leftIcon={<Icon as={PiPencilSimpleDuotone} boxSize="15px" />} onClick={confirmSheet.onOpen}
                  fontSize="12.5px" fontWeight="800" color="#0d9488" bg={drgba("#0d9488", 0.08)} borderRadius="10px" h="34px" px="13px"
                  _hover={{ bg: drgba("#0d9488", 0.16) }} _active={{ transform: "scale(0.97)" }}>Zmień</Button>
              }>Potwierdzanie głosu</SectionHead>
              <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="18px" direction={{ base: "column", sm: "row" }}>
                <Text fontSize="13.5px" color="#6b6480" lineHeight="1.55" maxW="440px">
                  <Text as="span" fontWeight="800" color="#241536">{curConfirm.label}</Text> — {curConfirm.desc}
                </Text>
                <HStack spacing="14px" flexShrink={0}>
                  <StateToggle value={model.voteConfirmation} onChange={v => model.setVoteConfirmation(v)} options={CONFIRM_OPTS} />
                </HStack>
              </Flex>
            </Box>
          </Collapse>

          {/* 4. Podpis tożsamością cyfrową — tylko dla „Tylko apka" (i przy tożsamości) */}
          <Collapse in={hasIdentity && isAppOnly} animateOpacity unmountOnExit>
            <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
              <SectionHead icon={PiFingerprintDuotone} color="#e51f44" right={
                <Button size="sm" variant="ghost" leftIcon={<Icon as={PiPencilSimpleDuotone} boxSize="15px" />} onClick={walletSheet.onOpen}
                  fontSize="12.5px" fontWeight="800" color="#e51f44" bg={drgba("#e51f44", 0.08)} borderRadius="10px" h="34px" px="13px"
                  _hover={{ bg: drgba("#e51f44", 0.16) }} _active={{ transform: "scale(0.97)" }}>Zmień</Button>
              }>Podpis tożsamością cyfrową</SectionHead>
              <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="18px" direction={{ base: "column", sm: "row" }}>
                <Text fontSize="13.5px" color="#6b6480" lineHeight="1.55" maxW="440px">
                  <Text as="span" fontWeight="800" color="#241536">{curWallet.label}</Text> — {curWallet.desc}
                </Text>
                <HStack spacing="14px" flexShrink={0}>
                  <StateToggle value={model.walletPolicy} onChange={v => model.setWalletPolicy(v)} options={WALLET_OPTS} />
                </HStack>
              </Flex>
            </Box>
          </Collapse>

          <Flex justify="center" pt="2px">
            <Button size="xs" variant="ghost" onClick={() => setSettingsOpen(false)}
              rightIcon={<Icon as={PiCaretDownBold} boxSize="10px" transform="rotate(180deg)" />}
              fontSize="12px" fontWeight="800" color="#8a8397" borderRadius="9px" px="12px"
              _hover={{ bg: "rgba(24,12,48,0.05)", color: "#5a5468" }}>
              Zwiń
            </Button>
          </Flex>
        </VStack>
        )}
        </Box>

        {/* ── Termin — inline w ciemniejszym tle, bez headera ── */}
        <Box id="cv-term" mt={{ base: "14px", md: "16px" }} bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
        {/* ── Zdanie o terminie (flex-wrap = równy rytm, brak skoków) ── */}
        <Flex position="relative" wrap="wrap" align="center" columnGap="8px" rowGap="10px" color="#2a2140" fontWeight="700" sx={sentenceFont} onClick={() => setSettingsOpen(false)}>
          <Text as="span" fontSize="inherit">Głosowanie ruszy</Text>

          {/* Start */}
          <Token color={START} icon={PiCalendarBlankDuotone} placeholder="wybierz start" invalid={!!startErr}
            title="Kiedy ruszy?" titleIcon={PiPlayCircleDuotone}
            value={startDate ? fmtShort(startDate) : undefined}>
            {close => (
              <VStack align="stretch" spacing="14px">
                <Box>
                  <PopLabel>Szybko</PopLabel>
                  <SimpleGrid columns={2} spacing="8px">
                    <QuickPill color={START} label="Za 15 min" onClick={() => { applyStart(defaultStart()); close(); }} />
                    <QuickPill color={START} label="Za godzinę" onClick={() => { const x = new Date(); x.setSeconds(0, 0); x.setMinutes(0); x.setHours(x.getHours() + 1); applyStart(x); close(); }} />
                    <QuickPill color={START} label="Jutro 9:00" onClick={() => { const x = new Date(); x.setDate(x.getDate() + 1); x.setHours(9, 0, 0, 0); applyStart(x); close(); }} />
                    <QuickPill color={START} label="Poniedz. 9:00" onClick={() => { applyStart(nextWeekday(1, 9)); close(); }} />
                  </SimpleGrid>
                </Box>
                <Box>
                  <PopLabel>Lub dokładnie</PopLabel>
                  <Input type="datetime-local" h="44px" borderRadius="12px" bg="rgba(24,12,48,0.03)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="700" fontSize="14px"
                    value={toInputValue(d.startVotingDate)} min={nowInputMin()}
                    _hover={{ borderColor: drgba(START, 0.4) }} _focusVisible={{ borderColor: START, boxShadow: `0 0 0 3px ${drgba(START, 0.16)}` }}
                    onChange={e => { const keep = durationMs ?? HOUR; fromInput(e.target.value, d.changeStartDate.bind(d)); const nd = e.target.value ? new Date(e.target.value) : null; if (nd) setDate(new Date(nd.getTime() + keep), d.changeEndDate.bind(d)); }}
                    onBlur={() => d.saveDateError(d.startVotingDate, VOTING_DATA_ERR_KEYS.BLUR_START)}
                  />
                </Box>
              </VStack>
            )}
          </Token>

          <Text as="span" fontSize="inherit">i potrwa</Text>

          {/* Czas trwania */}
          <Token color={DUR} icon={PiHourglassMediumDuotone} placeholder="jak długo" invalid={!!endErr}
            title="Ile trwa?" titleIcon={PiTimerDuotone}
            value={durationMs != null ? fmtDur(durationMs) : undefined}>
            {close => (
              <VStack align="stretch" spacing="14px">
                <Box>
                  <PopLabel>Presety</PopLabel>
                  <SimpleGrid columns={3} spacing="8px">
                    <QuickPill color={DUR} active={durationMs === 30 * MIN} label="30 min" onClick={() => { applyDuration(30 * MIN); close(); }} />
                    <QuickPill color={DUR} active={durationMs === HOUR} label="1 godz." onClick={() => { applyDuration(HOUR); close(); }} />
                    <QuickPill color={DUR} active={durationMs === 4 * HOUR} label="4 godz." onClick={() => { applyDuration(4 * HOUR); close(); }} />
                    <QuickPill color={DUR} active={durationMs === DAY} label="1 dzień" onClick={() => { applyDuration(DAY); close(); }} />
                    <QuickPill color={DUR} active={durationMs === 3 * DAY} label="3 dni" onClick={() => { applyDuration(3 * DAY); close(); }} />
                    <QuickPill color={DUR} active={durationMs === 7 * DAY} label="Tydzień" onClick={() => { applyDuration(7 * DAY); close(); }} />
                  </SimpleGrid>
                </Box>
                <Box>
                  <PopLabel>Własny</PopLabel>
                  <HStack spacing="8px">
                    <Input type="number" min={1} h="44px" w="88px" borderRadius="12px" bg="rgba(24,12,48,0.03)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="800" fontSize="14px" placeholder="np. 5"
                      _hover={{ borderColor: drgba(DUR, 0.4) }} _focusVisible={{ borderColor: DUR, boxShadow: `0 0 0 3px ${drgba(DUR, 0.16)}` }}
                      value={customVal} onChange={e => setCustomVal(e.target.value)} />
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap="4px" p="4px" borderRadius="12px" bg="rgba(24,12,48,0.045)">
                      {(["h", "d"] as const).map(u => (
                        <Box key={u} as="button" type="button" px="14px" py="9px" borderRadius="9px" fontWeight="800" fontSize="13px" transition={`all ${EASE}`}
                          bg={customUnit === u ? "white" : "transparent"} color={customUnit === u ? DUR : "#7a748a"}
                          boxShadow={customUnit === u ? "0 2px 8px -3px rgba(43,0,90,0.3)" : undefined}
                          onClick={() => setCustomUnit(u)}>{u === "h" ? "godz." : "dni"}</Box>
                      ))}
                    </Box>
                    <Box as="button" type="button" flex="1" h="44px" borderRadius="12px" bg={DUR} color="white" fontWeight="800" fontSize="13px" transition={`all ${EASE}`}
                      _hover={{ bg: "#6a25d8" }} _active={{ transform: "scale(0.97)" }}
                      onClick={() => { const n = parseInt(customVal, 10); if (n > 0) { applyDuration(n * (customUnit === "h" ? HOUR : DAY)); close(); } }}>Ustaw</Box>
                  </HStack>
                </Box>
              </VStack>
            )}
          </Token>

          <Text as="span" fontSize="inherit">.</Text>
        </Flex>

        {(startErr || endErr) && (
          <VStack align="stretch" spacing="8px" mt="16px" position="relative">
            {startErr && <FieldError>{startErr}</FieldError>}
            {endErr && <FieldError>{endErr}</FieldError>}
          </VStack>
        )}
        </Box>

        {/* ── Pytania (step 3) ── */}
        <Box id="cv-questions" mt={{ base: "34px", md: "44px" }}>
          <Flex align="center" justify="space-between" gap="12px" mb="14px">
            <HStack spacing="9px" minW={0}>
              <Icon as={PiListChecksDuotone} boxSize="18px" color="#7b2ff7" flexShrink={0} />
              <Text fontSize="14px" fontWeight="800" color="#241536" letterSpacing="-0.01em" noOfLines={1}>Pytania</Text>
            </HStack>
            <Box px="10px" py="3px" borderRadius="full" bg={drgba("#7b2ff7", 0.1)}>
              <Text fontSize="12.5px" fontWeight="800" color="#7b2ff7" whiteSpace="nowrap">{questionsCount}</Text>
            </Box>
          </Flex>

          <StepQuestions model={model} />
        </Box>

        {/* ── Uprawnieni do głosowania (step 4) ── */}
        <Box id="cv-voters" mt={{ base: "34px", md: "44px" }}>
          {/* heading: tytuł + przełącznik typu elektoratu */}
          <Flex align="center" justify="space-between" gap="12px" mb="14px" wrap="wrap" rowGap="10px">
            <HStack spacing="9px" minW={0}>
              <Icon as={PiUsersFourDuotone} boxSize="18px" color="#2563eb" flexShrink={0} />
              <Text fontSize="14px" fontWeight="800" color="#241536" letterSpacing="-0.01em" noOfLines={1}>Uprawnieni do głosowania</Text>
            </HStack>
            <Flex bg="rgba(24,12,48,0.05)" borderRadius="11px" p="4px" gap="3px" flexShrink={0}>
              {ELECTORATE.map(o => {
                const active = model.electorateType === o.value;
                return (
                  <Flex key={o.value} as="button" type="button" onClick={() => model.setElectorateType(o.value)} align="center" gap="6px" h="32px" px="12px" borderRadius="8px"
                    bg={active ? "white" : "transparent"} boxShadow={active ? "0 2px 8px -3px rgba(43,0,90,0.3)" : undefined} transition={`all ${EASE}`}>
                    <Icon as={o.icon} boxSize="15px" color={active ? o.color : "#9a94ab"} />
                    <Text fontSize="12.5px" fontWeight="800" color={active ? "#241536" : "#7a748a"}>{o.label}</Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>

          {model.electorateType === "signup" ? (
            /* ── Zapisy (vox populi) ── */
            <VStack align="stretch" spacing="12px">
              <Flex direction="column" align="center" textAlign="center" gap="14px" bg={drgba("#7b2ff7", 0.05)} border={`1.5px solid ${drgba("#7b2ff7", 0.16)}`} borderRadius="18px" p={{ base: "22px", md: "28px" }}>
                <Flex w="52px" h="52px" borderRadius="16px" bg={drgba("#7b2ff7", 0.12)} align="center" justify="center"><Icon as={PiUserPlusDuotone} boxSize="26px" color="#7b2ff7" /></Flex>
                <Box>
                  <Text fontSize="15px" fontWeight="800" color="#241536" letterSpacing="-0.01em">Publiczna rejestracja (vox populi)</Text>
                  <Text fontSize="13px" color="#8a8397" mt="4px" maxW="420px" mx="auto">Powstanie publiczna strona zapisów — udostępniasz link, a ludzie rejestrują się z potwierdzeniem e-mail. Po zamknięciu lista staje się elektoratem.</Text>
                </Box>
              </Flex>
              <Box bg="rgba(24,12,48,0.028)" borderRadius="16px" p={{ base: "16px", md: "18px" }}>
                <HStack spacing="8px" mb="12px"><Icon as={PiCalendarBlankDuotone} boxSize="16px" color="#7b2ff7" /><Text fontSize="13.5px" fontWeight="800" color="#241536">Zapisy zamykają się</Text></HStack>
                <Input type="datetime-local" h="46px" maxW="340px" borderRadius="12px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="700" fontSize="14px"
                  value={model.signupClosesAt} min={nowInputMin()} max={evStartLocal}
                  onChange={e => model.setSignupClosesAt(e.target.value)}
                  _hover={{ borderColor: drgba("#7b2ff7", 0.4) }} _focusVisible={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }} />
                {evStartDate ? (
                  <Wrap spacing="8px" mt="12px" align="center">
                    <WrapItem alignItems="center"><Text fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing="0.04em" color="#a29bb2" pr="2px">Przed startem</Text></WrapItem>
                    <WrapItem><QuickPill color="#7b2ff7" label="Godzinę" onClick={() => setSignupBefore(60 * 60 * 1000)} /></WrapItem>
                    <WrapItem><QuickPill color="#7b2ff7" label="6 godz." onClick={() => setSignupBefore(6 * 60 * 60 * 1000)} /></WrapItem>
                    <WrapItem><QuickPill color="#7b2ff7" label="Dzień" onClick={() => setSignupBefore(24 * 60 * 60 * 1000)} /></WrapItem>
                    <WrapItem><QuickPill color="#7b2ff7" label="Na start" onClick={() => evStartLocal && model.setSignupClosesAt(evStartLocal)} /></WrapItem>
                  </Wrap>
                ) : (
                  <Text fontSize="12.5px" color="#a29bb2" mt="8px">Najpierw ustaw start w sekcji „Termin" — zaproponujemy zamknięcie zapisów.</Text>
                )}
              </Box>
            </VStack>
          ) : model.electorateType === "open" ? (
            /* ── Otwarte (referendum) ── */
            <Flex direction="column" align="center" textAlign="center" gap="14px" bg={drgba("#10b981", 0.05)} border={`1.5px solid ${drgba("#10b981", 0.16)}`} borderRadius="18px" p={{ base: "26px", md: "32px" }}>
              <Flex w="52px" h="52px" borderRadius="16px" bg={drgba("#10b981", 0.12)} align="center" justify="center"><Icon as={PiGlobeDuotone} boxSize="26px" color="#10b981" /></Flex>
              <Box>
                <Text fontSize="15px" fontWeight="800" color="#241536" letterSpacing="-0.01em">Referendum otwarte</Text>
                <Text fontSize="13px" color="#8a8397" mt="4px" maxW="420px" mx="auto">Głosuje każda osoba zweryfikowana przez mObywatel — nie tworzysz listy uprawnionych.</Text>
              </Box>
              <Wrap spacing="8px" justify="center">
                {[{ i: PiSealCheckDuotone, l: "mObywatel" }, { i: PiUsersThreeDuotone, l: "1 obywatel = 1 głos" }, { i: PiListChecksDuotone, l: "Bez listy" }].map(f => (
                  <WrapItem key={f.l}>
                    <HStack spacing="6px" px="11px" py="6px" borderRadius="full" bg="rgba(255,255,255,0.7)" border={`1px solid ${drgba("#10b981", 0.2)}`}>
                      <Icon as={f.i} boxSize="14px" color="#10b981" />
                      <Text fontSize="12px" fontWeight="700" color="#0f7a52">{f.l}</Text>
                    </HStack>
                  </WrapItem>
                ))}
              </Wrap>
            </Flex>
          ) : (
            /* ── Zamknięta lista ── */
            <>
              {model.eligibleVotersModel.votersEmails.length > 0 && (
                <Flex justify="flex-end" mb="10px">
                  <HStack spacing="10px">
                    <AvatarGroup size="sm" max={4} spacing="-10px">
                      {model.eligibleVotersModel.votersEmails.slice(0, 8).map(e => (
                        <Avatar key={e.id} name={e.value} bg="#2563eb" color="white" borderColor="white" />
                      ))}
                    </AvatarGroup>
                    <Box px="10px" py="3px" borderRadius="full" bg={drgba("#2563eb", 0.1)}>
                      <Text fontSize="12.5px" fontWeight="800" color="#2563eb" whiteSpace="nowrap">~{model.eligibleVotersModel.totalCount}</Text>
                    </Box>
                  </HStack>
                </Flex>
              )}

              <input type="file" accept=".csv,text/csv" ref={csvRef} style={{ display: "none" }} onChange={e => onPickCsv(e.target.files?.[0])} />
              <Flex align="center" gap="4px" bg="rgba(24,12,48,0.04)" borderRadius="13px" p="5px" flexWrap="wrap">
                <Button size="xs" h="32px" px="11px" variant="ghost" leftIcon={<Icon as={PiMagnifyingGlassDuotone} boxSize="14px" />} onClick={votersModal.onOpen} borderRadius="9px" fontSize="12.5px" fontWeight="700" color="#4a4458" _hover={{ bg: "white", color: "#2563eb" }}>Szukaj kont</Button>
                <Button size="xs" h="32px" px="11px" variant="ghost" leftIcon={<Icon as={PiUsersThreeDuotone} boxSize="14px" />} onClick={groupsSheet.onOpen} borderRadius="9px" fontSize="12.5px" fontWeight="700" color="#4a4458" _hover={{ bg: "white", color: "#2563eb" }}>Z grup</Button>
                <Button size="xs" h="32px" px="11px" variant="ghost" leftIcon={<Icon as={PiUserPlusDuotone} boxSize="14px" />} onClick={votersModal.onOpen} borderRadius="9px" fontSize="12.5px" fontWeight="700" color="#4a4458" _hover={{ bg: "white", color: "#7b2ff7" }}>Ręcznie</Button>
                <Button size="xs" h="32px" px="11px" variant="ghost" leftIcon={<Icon as={PiUploadSimpleDuotone} boxSize="14px" />} onClick={() => csvRef.current?.click()} borderRadius="9px" fontSize="12.5px" fontWeight="700" color="#4a4458" _hover={{ bg: "white", color: "#10b981" }}>Wgraj CSV</Button>
                <Button size="xs" h="32px" px="11px" ml="auto" variant="ghost" leftIcon={<Icon as={PiDownloadSimpleDuotone} boxSize="14px" />} onClick={() => model.eligibleVotersModel.downloadCsvTemplate()} borderRadius="9px" fontSize="12.5px" fontWeight="700" color="#8a8397" _hover={{ bg: "white", color: "#5a5468" }}>Szablon</Button>
              </Flex>

              {/* Załączony CSV */}
              {model.eligibleVotersModel.hasCsv && (
                <Flex align="center" gap="10px" mt="10px" p="10px 13px" borderRadius="12px" bg={drgba("#10b981", 0.08)} border={`1px solid ${drgba("#10b981", 0.22)}`}>
                  <Icon as={PiFileTextDuotone} boxSize="17px" color="#10b981" flexShrink={0} />
                  <Text fontSize="13px" fontWeight="700" color="#241536" noOfLines={1} flex="1">{model.eligibleVotersModel.votersCsv?.name ?? model.eligibleVotersModel.votersCsvUrlName}</Text>
                  <Box as="button" type="button" onClick={() => model.eligibleVotersModel.setVotersCsv(undefined)} p="6px" borderRadius="8px" color="#c41a3a" _hover={{ bg: drgba("#e51f44", 0.1) }} flexShrink={0}><Icon as={PiTrashDuotone} boxSize="15px" /></Box>
                </Flex>
              )}

              {model.eligibleVotersModel.votersEmails.length === 0 && model.eligibleVotersModel.selectedGroupIds.length === 0 && !model.eligibleVotersModel.hasCsv && (
                <Flex mt="12px" direction="column" align="center" textAlign="center" gap="16px" border="1.5px dashed rgba(24,12,48,0.14)" borderRadius="16px" bg="rgba(24,12,48,0.015)" p={{ base: "24px", md: "32px" }}>
                  <Flex w="52px" h="52px" borderRadius="16px" bg={drgba("#2563eb", 0.1)} align="center" justify="center">
                    <Icon as={PiUsersFourDuotone} boxSize="26px" color="#2563eb" />
                  </Flex>
                  <Box>
                    <Text fontSize="15px" fontWeight="800" color="#241536" letterSpacing="-0.01em">Nie dodano jeszcze nikogo</Text>
                    <Text fontSize="13px" color="#8a8397" mt="4px" maxW="380px">Dodaj uprawnionych — wyszukaj konta, wybierz grupę albo wgraj listę CSV.</Text>
                  </Box>
                  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="10px" w="100%" maxW="500px">
                    <QuickAction icon={PiMagnifyingGlassDuotone} label="Szukaj kont" color="#2563eb" onClick={votersModal.onOpen} />
                    <QuickAction icon={PiUsersThreeDuotone} label="Z grupy" color="#7b2ff7" onClick={groupsSheet.onOpen} />
                    <QuickAction icon={PiUploadSimpleDuotone} label="Wgraj CSV" color="#10b981" onClick={() => csvRef.current?.click()} />
                  </SimpleGrid>
                </Flex>
              )}
            </>
          )}
        </Box>

        {/* ── Dodatki (opcjonalne) — kworum + dokumenty ── */}
        <Box id="cv-extras" mt={{ base: "34px", md: "44px" }}>
          <HStack spacing="9px" mb="14px">
            <Icon as={PiPuzzlePieceDuotone} boxSize="18px" color="#f59e0b" />
            <Text fontSize="14px" fontWeight="800" color="#241536" letterSpacing="-0.01em">Dodatki</Text>
            <Text fontSize="12px" fontWeight="700" color="#a29bb2">opcjonalne</Text>
          </HStack>

          <VStack align="stretch" spacing="12px">
            {/* Kworum */}
            <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "20px" }}>
              <Flex align="center" justify="space-between" gap="14px">
                <HStack spacing="11px" minW={0}>
                  <Flex w="34px" h="34px" borderRadius="10px" bg={drgba("#f59e0b", 0.12)} align="center" justify="center" flexShrink={0}>
                    <Icon as={PiPercentDuotone} boxSize="17px" color="#f59e0b" />
                  </Flex>
                  <Box minW={0}>
                    <Text fontSize="14px" fontWeight="800" color="#241536">Kworum</Text>
                    <Text fontSize="12.5px" color="#8a8397" noOfLines={1}>Minimalna frekwencja, by głosowanie było ważne.</Text>
                  </Box>
                </HStack>
                <Switch size="lg" colorScheme="orange" isChecked={model.quorumPercent != null} onChange={e => model.setQuorum(e.target.checked ? 50 : undefined)} flexShrink={0} />
              </Flex>
              <Collapse in={model.quorumPercent != null} animateOpacity unmountOnExit>
                <Flex align="center" gap="18px" pt="18px">
                  <Slider aria-label="Kworum" min={0} max={100} step={5} value={model.quorumPercent ?? 50} onChange={v => model.setQuorum(v)} focusThumbOnChange={false} flex="1">
                    <SliderTrack bg="rgba(24,12,48,0.09)" h="6px" borderRadius="full"><SliderFilledTrack bg="#f59e0b" /></SliderTrack>
                    <SliderThumb boxSize="20px" border="2px solid" borderColor="#f59e0b" />
                  </Slider>
                  <Text fontSize="17px" fontWeight="800" color="#241536" w="52px" textAlign="right">{model.quorumPercent ?? 50}%</Text>
                </Flex>
              </Collapse>
            </Box>

            {/* Dokumenty */}
            <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "20px" }}>
              <HStack spacing="9px" mb="14px">
                <Icon as={PiFileTextDuotone} boxSize="17px" color="#430098" />
                <Text fontSize="14px" fontWeight="800" color="#241536">Dokumenty do akceptacji</Text>
              </HStack>
              <VStack align="stretch" spacing="9px">
                <DocRow label="Regulamin głosowania" color="#430098"
                  hasFile={!!(model.customRegulations || model.customRegulationsUrl)}
                  fileName={model.customRegulations?.name ?? model.customRegulationsUrlName}
                  onPick={f => model.setCustomRegulations(f)} />
                <DocRow label="Polityka prywatności" color="#0d9488"
                  hasFile={!!(model.customPrivacyPolicy || model.customPrivacyPolicyUrl)}
                  fileName={model.customPrivacyPolicy?.name ?? model.customPrivacyPolicyUrlName}
                  onPick={f => model.setCustomPrivacyPolicy(f)} />
              </VStack>
              <Text fontSize="12px" color="#a29bb2" mt="10px">Pominięte = używamy dokumentów platformy.</Text>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* ── Modal: wyszukiwarka kont / wklej listę ── */}
      <AddVotersModal model={model.eligibleVotersModel} isOpen={votersModal.isOpen} onClose={votersModal.onClose} />

      {/* ── Bottom sheet: wybór grup ── */}
      <Drawer isOpen={groupsSheet.isOpen} onClose={groupsSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(24,12,48,0.45)" sx={{ backdropFilter: "blur(4px)" }} />
        <DrawerContent bg="rgba(255,255,255,0.98)" borderRadius="26px" mx="auto" mb="16px" boxShadow="0 -10px 50px -20px rgba(43,0,90,0.5)" pb="calc(env(safe-area-inset-bottom) + 8px)" sx={{ width: "min(560px, calc(100% - 32px)) !important", maxWidth: "560px !important" }}>
          <Box pt="12px" display="flex" justifyContent="center"><Box w="42px" h="5px" borderRadius="full" bg="rgba(24,12,48,0.14)" /></Box>
          <DrawerCloseButton top="16px" right="16px" borderRadius="full" />
          <DrawerHeader px={{ base: "20px", md: "28px" }} pt="14px" pb="6px">
            <HStack spacing="9px">
              <Icon as={PiUsersThreeDuotone} boxSize="20px" color="#2563eb" />
              <Text fontSize="18px" fontWeight="900" color="#1b1030" letterSpacing="-0.01em">Wybierz grupy</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody px={{ base: "20px", md: "28px" }} py="12px" maxH="60vh" overflowY="auto">
            {groupsLoading ? (
              <Flex justify="center" py="30px"><Spinner color="#2563eb" /></Flex>
            ) : model.eligibleVotersModel.groups.length === 0 ? (
              <Flex direction="column" align="center" textAlign="center" gap="8px" py="26px">
                <Flex w="48px" h="48px" borderRadius="14px" bg={drgba("#2563eb", 0.1)} align="center" justify="center"><Icon as={PiUsersThreeDuotone} boxSize="24px" color="#2563eb" /></Flex>
                <Text fontSize="14px" fontWeight="800" color="#241536">Brak grup</Text>
                <Text fontSize="13px" color="#8a8397" maxW="320px">Utwórz grupę w sekcji „Grupy", aby wybierać ją tutaj.</Text>
              </Flex>
            ) : (
              <VStack align="stretch" spacing="8px">
                {model.eligibleVotersModel.groups.map(g => {
                  const selected = model.eligibleVotersModel.selectedGroupIds.includes(g.id);
                  return (
                    <Flex key={g.id} as="button" type="button" onClick={() => model.eligibleVotersModel.toggleGroup(g.id)} align="center" gap="12px" p="12px 14px" borderRadius="12px" textAlign="left"
                      border="1.5px solid" borderColor={selected ? "#2563eb" : "rgba(24,12,48,0.1)"} bg={selected ? drgba("#2563eb", 0.06) : "rgba(255,255,255,0.6)"} transition={`all ${EASE}`} _hover={{ borderColor: "#2563eb" }}>
                      <Flex w="22px" h="22px" borderRadius="7px" border="2px solid" borderColor={selected ? "#2563eb" : "#cbd5e1"} bg={selected ? "#2563eb" : "transparent"} align="center" justify="center" flexShrink={0}>
                        {selected && <Icon as={PiCheckBold} color="white" boxSize="12px" />}
                      </Flex>
                      <Icon as={PiUsersThreeDuotone} color="#2563eb" boxSize="18px" flexShrink={0} />
                      <Text flex="1" fontSize="14px" fontWeight="700" color="#241536" noOfLines={1}>{g.name}</Text>
                      <Text fontSize="12.5px" fontWeight="700" color="#8a8397" flexShrink={0}>{g.members_count} os.</Text>
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </DrawerBody>
          <DrawerFooter px={{ base: "20px", md: "28px" }} pt="6px">
            <Button w="100%" h="50px" bg="primary" color="white" borderRadius="14px" fontWeight="800" fontSize="15px" onClick={groupsSheet.onClose} _hover={{ bg: "#36007a" }} _active={{ transform: "scale(0.99)" }}>Gotowe</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ── Bottom sheet: Widoczność i tajność ── */}
      <Drawer isOpen={visSheet.isOpen} onClose={visSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(24,12,48,0.45)" sx={{ backdropFilter: "blur(4px)" }} />
        <DrawerContent
          bg="rgba(255,255,255,0.98)"
          borderRadius="26px"
          mx="auto"
          mb="16px"
          boxShadow="0 -10px 50px -20px rgba(43,0,90,0.5)"
          pb="calc(env(safe-area-inset-bottom) + 8px)"
          sx={{ width: "min(560px, calc(100% - 32px)) !important", maxWidth: "560px !important" }}
        >
          <Box pt="12px" display="flex" justifyContent="center"><Box w="42px" h="5px" borderRadius="full" bg="rgba(24,12,48,0.14)" /></Box>
          <DrawerCloseButton top="16px" right="16px" borderRadius="full" />
          <DrawerHeader px={{ base: "20px", md: "28px" }} pt="14px" pb="6px">
            <HStack spacing="9px">
              <Icon as={PiEyeDuotone} boxSize="20px" color="#2563eb" />
              <Text fontSize="18px" fontWeight="900" color="#1b1030" letterSpacing="-0.01em">Widoczność i tajność</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody px={{ base: "20px", md: "28px" }} py="16px">
            <Box>
              <SubLabel>Kto widzi głosowanie</SubLabel>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="10px">
                <OptionCard active={model.isPublic === false} onClick={() => model.setIsPublic(false)} opt={{ label: "Niepubliczne", desc: "Temat i wyniki widzą tylko zaproszeni.", icon: PiEyeSlashDuotone, color: "#6b7280" }} />
                <OptionCard active={model.isPublic === true} onClick={() => model.setIsPublic(true)} opt={{ label: "Publiczne", desc: "Temat, pytania i wyniki widzi każdy.", icon: PiGlobeDuotone, color: "#2563eb" }} />
              </SimpleGrid>
            </Box>
            <Box mt="22px">
              <SubLabel>Tajność głosu</SubLabel>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="10px">
                <OptionCard active={model.isSecret === true} onClick={() => model.setIsSecret(true)} opt={{ label: "Tajne", desc: "Nie widać, kto jak zagłosował (ZK).", icon: PiLockDuotone, color: "#7b2ff7" }} />
                <OptionCard active={model.isSecret === false} onClick={() => model.setIsSecret(false)} opt={{ label: "Jawne imienne", desc: "Widać głos każdej osoby — jak w radzie.", icon: PiUsersThreeDuotone, color: "#f59e0b" }} />
              </SimpleGrid>
            </Box>
          </DrawerBody>
          <DrawerFooter px={{ base: "20px", md: "28px" }} pt="6px">
            <Button w="100%" h="50px" bg="primary" color="white" borderRadius="14px" fontWeight="800" fontSize="15px" onClick={visSheet.onClose} _hover={{ bg: "#36007a" }} _active={{ transform: "scale(0.99)" }}>
              Gotowe
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ── Bottom sheet: Wymagana tożsamość wyborcy ── */}
      <Drawer isOpen={identitySheet.isOpen} onClose={identitySheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(24,12,48,0.45)" sx={{ backdropFilter: "blur(4px)" }} />
        <DrawerContent
          bg="rgba(255,255,255,0.98)"
          borderRadius="26px"
          mx="auto"
          mb="16px"
          boxShadow="0 -10px 50px -20px rgba(43,0,90,0.5)"
          pb="calc(env(safe-area-inset-bottom) + 8px)"
          sx={{ width: "min(600px, calc(100% - 32px)) !important", maxWidth: "600px !important" }}
        >
          <Box pt="12px" display="flex" justifyContent="center"><Box w="42px" h="5px" borderRadius="full" bg="rgba(24,12,48,0.14)" /></Box>
          <DrawerCloseButton top="16px" right="16px" borderRadius="full" />
          <DrawerHeader px={{ base: "20px", md: "28px" }} pt="14px" pb="6px">
            <HStack spacing="9px">
              <Icon as={PiIdentificationCardDuotone} boxSize="20px" color="#10b981" />
              <Text fontSize="18px" fontWeight="900" color="#1b1030" letterSpacing="-0.01em">Wymagana tożsamość wyborcy</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody px={{ base: "20px", md: "28px" }} py="16px">
            <SubLabel>Jak mocna musi być tożsamość, by oddać głos</SubLabel>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="10px">
              {IDENTITY_OPTS.map(o => (
                <OptionCard key={o.value} active={identityValue === o.value} onClick={() => applyIdentity(o.value)} opt={o} />
              ))}
            </SimpleGrid>
          </DrawerBody>
          <DrawerFooter px={{ base: "20px", md: "28px" }} pt="6px">
            <Button w="100%" h="50px" bg="primary" color="white" borderRadius="14px" fontWeight="800" fontSize="15px" onClick={identitySheet.onClose} _hover={{ bg: "#36007a" }} _active={{ transform: "scale(0.99)" }}>
              Gotowe
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ── Bottom sheet: Potwierdzanie głosu ── */}
      <Drawer isOpen={confirmSheet.isOpen} onClose={confirmSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(24,12,48,0.45)" sx={{ backdropFilter: "blur(4px)" }} />
        <DrawerContent
          bg="rgba(255,255,255,0.98)"
          borderRadius="26px"
          mx="auto"
          mb="16px"
          boxShadow="0 -10px 50px -20px rgba(43,0,90,0.5)"
          pb="calc(env(safe-area-inset-bottom) + 8px)"
          sx={{ width: "min(600px, calc(100% - 32px)) !important", maxWidth: "600px !important" }}
        >
          <Box pt="12px" display="flex" justifyContent="center"><Box w="42px" h="5px" borderRadius="full" bg="rgba(24,12,48,0.14)" /></Box>
          <DrawerCloseButton top="16px" right="16px" borderRadius="full" />
          <DrawerHeader px={{ base: "20px", md: "28px" }} pt="14px" pb="6px">
            <HStack spacing="9px">
              <Icon as={PiSealCheckDuotone} boxSize="20px" color="#0d9488" />
              <Text fontSize="18px" fontWeight="900" color="#1b1030" letterSpacing="-0.01em">Potwierdzanie głosu</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody px={{ base: "20px", md: "28px" }} py="16px">
            <SubLabel>Jak wyborca potwierdza głos</SubLabel>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="10px">
              {CONFIRM_OPTS.map(o => (
                <OptionCard key={o.value} active={model.voteConfirmation === o.value} onClick={() => model.setVoteConfirmation(o.value)} opt={o} />
              ))}
            </SimpleGrid>
          </DrawerBody>
          <DrawerFooter px={{ base: "20px", md: "28px" }} pt="6px">
            <Button w="100%" h="50px" bg="primary" color="white" borderRadius="14px" fontWeight="800" fontSize="15px" onClick={confirmSheet.onClose} _hover={{ bg: "#36007a" }} _active={{ transform: "scale(0.99)" }}>
              Gotowe
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ── Bottom sheet: Podpis tożsamością cyfrową ── */}
      <Drawer isOpen={walletSheet.isOpen} onClose={walletSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(24,12,48,0.45)" sx={{ backdropFilter: "blur(4px)" }} />
        <DrawerContent
          bg="rgba(255,255,255,0.98)"
          borderRadius="26px"
          mx="auto"
          mb="16px"
          boxShadow="0 -10px 50px -20px rgba(43,0,90,0.5)"
          pb="calc(env(safe-area-inset-bottom) + 8px)"
          sx={{ width: "min(600px, calc(100% - 32px)) !important", maxWidth: "600px !important" }}
        >
          <Box pt="12px" display="flex" justifyContent="center"><Box w="42px" h="5px" borderRadius="full" bg="rgba(24,12,48,0.14)" /></Box>
          <DrawerCloseButton top="16px" right="16px" borderRadius="full" />
          <DrawerHeader px={{ base: "20px", md: "28px" }} pt="14px" pb="6px">
            <HStack spacing="9px">
              <Icon as={PiFingerprintDuotone} boxSize="20px" color="#e51f44" />
              <Text fontSize="18px" fontWeight="900" color="#1b1030" letterSpacing="-0.01em">Podpis tożsamością cyfrową</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody px={{ base: "20px", md: "28px" }} py="16px">
            <SubLabel>Jak mocno wymagać podpisu kluczem tożsamości</SubLabel>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="10px">
              {WALLET_OPTS.map(o => (
                <OptionCard key={o.value} active={model.walletPolicy === o.value} onClick={() => model.setWalletPolicy(o.value)} opt={o} />
              ))}
            </SimpleGrid>
          </DrawerBody>
          <DrawerFooter px={{ base: "20px", md: "28px" }} pt="6px">
            <Button w="100%" h="50px" bg="primary" color="white" borderRadius="14px" fontWeight="800" fontSize="15px" onClick={walletSheet.onClose} _hover={{ bg: "#36007a" }} _active={{ transform: "scale(0.99)" }}>
              Gotowe
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </motion.div>
  );
});
